import React from 'react';

import { Surface } from '../primitives';
import { KANJI_PATHS } from './kanjiPaths';

import type { WatermarkKanji } from './kanjiPaths';
import type {
  KanjiWatermarkProps,
  KanjiWatermarkShape,
} from './KanjiWatermark.types';

const SHAPE_CLASSES: Record<KanjiWatermarkShape, string> = {
  landscape: 'aspect-4/3',
  square: 'aspect-square',
  phone: 'aspect-9/19',
};

/**
 * Corners: the plates take a card's, the phone a handset's. Surface stops at
 * `xl`, so the phone's corner is a class of its own.
 */
const ROUNDED: Record<
  KanjiWatermarkShape,
  { surface: 'xl' | 'none'; className: string }
> = {
  landscape: { surface: 'xl', className: '' },
  square: { surface: 'xl', className: '' },
  phone: { surface: 'none', className: 'rounded-2xl' },
};

/**
 * The glyph's size, as a share of the plate's width. There's no spacing token
 * this large, and a plate's width varies with its page, so the glyph is sized
 * by the plate itself (a container query unit): it keeps its proportion in any
 * plate, and in one grown taller than its shape. The box is the glyph's em
 * square, the same box the live text used to fill.
 */
const GLYPH_CLASSES: Record<KanjiWatermarkShape, string> = {
  landscape: 'size-[35cqw]',
  square: 'size-[40cqw]',
  phone: 'size-[58cqw]',
};

/**
 * Each outline as a CSS mask. The glyph is a mask over a block painted with
 * the watermark gradient token, so the fade is the token itself rather than a
 * copy of it in SVG stops.
 */
const GLYPH_MASKS = Object.fromEntries(
  Object.entries(KANJI_PATHS).map(([kanji, path]) => {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 1000"><path d="${path}"/></svg>`;
    return [kanji, `url("data:image/svg+xml,${encodeURIComponent(svg)}")`];
  })
) as Record<WatermarkKanji, string>;

/**
 * KanjiWatermark - a plate with one large kanji fading in from the top, over
 * a faint sakura glow: the brand's quiet visual beside copy. Decorative, so
 * hidden from assistive tech. The kanji is drawn from an outline, not set as
 * text, so it looks the same without a Japanese font installed.
 *
 * @example
 * // The auth brand panel, where the plate grows beside a long form
 * <KanjiWatermark kanji="門" className="grow" />
 *
 * // A square plate
 * <KanjiWatermark kanji="道" shape="square" />
 *
 * // A phone, with its notch drawn over the plate
 * <KanjiWatermark kanji="道" shape="phone" className="w-52">
 *   <Notch />
 * </KanjiWatermark>
 */
export function KanjiWatermark({
  kanji,
  shape = 'landscape',
  children,
  className = '',
}: KanjiWatermarkProps): React.JSX.Element {
  const rounded = ROUNDED[shape];

  return (
    <Surface
      level="base"
      rounded={rounded.surface}
      border
      className={`@container relative overflow-hidden ${SHAPE_CLASSES[shape]} ${rounded.className} ${className}`
        .replace(/\s+/g, ' ')
        .trim()}
    >
      <div
        className="absolute inset-0 flex items-center justify-center"
        aria-hidden="true"
      >
        <span
          data-testid="kanji-watermark-glyph"
          data-kanji={kanji}
          className={`block bg-gradient-sakura-watermark ${GLYPH_CLASSES[shape]}`}
          style={{
            maskImage: GLYPH_MASKS[kanji],
            maskSize: 'contain',
            maskRepeat: 'no-repeat',
            WebkitMaskImage: GLYPH_MASKS[kanji],
            WebkitMaskSize: 'contain',
            WebkitMaskRepeat: 'no-repeat',
          }}
        />
      </div>
      <div
        className="absolute bottom-0 left-1/2 h-1/4 w-1/2 -translate-x-1/2 rounded-full bg-primary-500 opacity-20 blur-3xl"
        aria-hidden="true"
      />
      {children ? <div aria-hidden="true">{children}</div> : null}
    </Surface>
  );
}

export default KanjiWatermark;
